// filepath: apps/web/src/operator/isOperatorSurface.ts
//
// FR-UI-OPS-1 AC-1 — pure routing-decision helper.
//
// Decides whether the current request should mount the operator
// surface (OperatorRoot) instead of the tenant SPA shell. The
// decision is made from hostname + pathname only — these are the
// two immutable browser-supplied signals; no other state is
// consulted. The helper is invoked from `App.tsx` BEFORE
// `<TenantProvider>` mounts, so a positive result bypasses the OIDC
// bootstrap entirely.
//
// INVARIANTS
//   - hostname MUST match the platform apex exactly (after
//     normalization). The apex is read from
//     `import.meta.env.VITE_PLATFORM_APEX` (build-time, set via
//     Vite's `define` replacement at the platform's deployment).
//     A safe fallback `"platform.example"` keeps unit tests stable
//     when the env var is absent.
//   - pathname MUST begin with `/operator` (the operator surface
//     root). The pathname is normalized to drop trailing slashes
//     so `/operator` and `/operator/` both route to the operator.
//   - Hostname normalization strips:
//       * leading "www." (a tenant may route through a www-cname
//         and the apex should still be detected)
//       * trait ":" port suffix (so local dev at apex:8080 is
//         still detected)
//       * surrounding whitespace.
//
//   The helper is intentionally conservative: a hostname that does
//   not match the apex (after normalization) returns false,
//   regardless of how the path is shaped. This means the operator
//   surface is NEVER mounted on a tenant subdomain.

const DEFAULT_PLATFORM_APEX = "platform.example";

/**
 * Read the platform apex from the build-time env var, with a safe
 * fallback. Returns the trimmed value or the default if the env
 * var is absent / empty.
 */
function readPlatformApex(): string {
  // The `import.meta.env` typing is augmented by `vite/client` so
  // VITE_* vars are accessible at type-check time. Use a string
  // guard so a misconfigured env (e.g. undefined) does not throw.
  const raw = (import.meta.env.VITE_PLATFORM_APEX ?? "") as string;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return DEFAULT_PLATFORM_APEX;
  return trimmed;
}

/**
 * Normalize a hostname for an exact (case-insensitive) apex match.
 *   - Trims whitespace.
 *   - Lowercases (DNS hostnames are case-insensitive).
 *   - Strips a leading "www." label.
 *   - Strips a trailing ":<port>" suffix.
 *
 * Returns "" if the input is empty / whitespace.
 */
export function normalizeHostname(hostname: string): string {
  if (typeof hostname !== "string") return "";
  let h = hostname.trim().toLowerCase();
  if (h.length === 0) return "";
  if (h.startsWith("www.")) {
    h = h.slice(4);
  }
  const colon = h.indexOf(":");
  if (colon >= 0) {
    h = h.slice(0, colon);
  }
  return h;
}

/**
 * Normalize a pathname for a prefix match.
 *   - Trims whitespace.
 *   - Drops a trailing "/" (except the bare "/" case).
 *
 * Returns "/" for empty input.
 */
export function normalizePathname(pathname: string): string {
  if (typeof pathname !== "string") return "/";
  let p = pathname.trim();
  if (p.length === 0) return "/";
  if (p.length > 1 && p.endsWith("/")) {
    p = p.slice(0, -1);
  }
  return p;
}

/**
 * Decide whether the current location should mount the operator
 * surface. True iff the (normalized) hostname equals the platform
 * apex AND the (normalized) pathname starts with "/operator".
 *
 * Pure: no side effects, no global state. Safe to call from React
 * render or a useState initializer.
 */
export function isOperatorSurface(hostname: string, pathname: string): boolean {
  const apex = readPlatformApex().toLowerCase();
  const normalizedHost = normalizeHostname(hostname);
  const normalizedPath = normalizePathname(pathname);
  if (normalizedHost !== apex) return false;
  return (
    normalizedPath === "/operator" || normalizedPath.startsWith("/operator/")
  );
}
